import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useAppStore } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { useSettings } from '../menu';
import { Icon } from '../icons/Icon';
import { LanguageSwitcher } from './LanguageSwitcher';
import { T } from './Text';
import { TextField } from './TextField';
import { ThemeToggle } from './ThemeToggle';
import { TextSizePicker } from './TextSizePicker';

/**
 * Shown when a backend is configured but nobody is signed in. Staff can skip it
 * and keep taking orders on this device alone — the outbox holds everything
 * until someone signs in.
 *
 * There is no sign-up here, by design. An owner creates every login in Admin,
 * email and first password together, and says them out loud. Nothing in this
 * app sends mail, so a self-service account — or a self-service password reset
 * — would have no way to prove who was asking.
 */
export function SignInScreen({ onSkip }: { onSkip: () => void }) {
  const colors = useColors();
  const t = useT();
  const settings = useSettings();
  const signIn = useAppStore((s) => s.signIn);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgot, setShowForgot] = useState(false);

  const submit = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    const err = await signIn(email, password);
    setBusy(false);
    if (err) setError(err);
  };

  return (
    <ScrollView
      // flex/minHeight bound the frame to the screen; flexGrow on the content
      // centres the card when it is short and lets it scroll when the keyboard
      // is up on a small phone. Both halves are needed — see components/Screen.
      style={{ flex: 1, minHeight: 0, backgroundColor: colors.bg }}
      contentContainerStyle={{
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      keyboardShouldPersistTaps="handled"
    >
      <View style={{ width: '100%', maxWidth: 380, gap: 18 }}>
        <View style={{ alignItems: 'center', gap: 10 }}>
          <View
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: colors.goldDim,
              backgroundColor: colors.surface,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="store" size={26} color={colors.gold} />
          </View>
          <T size={24} weight={600} serif color={colors.gold} numberOfLines={2}>
            {settings.brandName}
          </T>
          <T size={12.5} color={colors.textFaint} style={{ textAlign: 'center' }}>
            {t('sign_in_subtitle')}
          </T>
        </View>

        <View
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.line,
            borderRadius: radius.lg,
            padding: 18,
            gap: 14,
          }}
        >
          <TextField
            testID="sign-in-email"
            label={t('email')}
            icon="user"
            value={email}
            onChangeText={setEmail}
            placeholder="name@example.com"
            keyboardType="email-address"
          />
          <TextField
            testID="sign-in-password"
            label={t('password')}
            icon="lock"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            onSubmitEditing={submit}
          />

          {error ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Icon name="close" size={14} color={colors.spice} />
              <T size={12} color={colors.spice} style={{ flexShrink: 1 }}>
                {error}
              </T>
            </View>
          ) : null}

          <Pressable
            onPress={submit}
            testID="sign-in-submit"
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 9,
              paddingVertical: 14,
              borderRadius: radius.md,
              backgroundColor: colors.gold,
              opacity: pressed || busy ? 0.85 : 1,
            })}
          >
            <Icon name="check" size={17} color={colors.onGold} />
            <T size={14.5} weight={800} color={colors.onGold}>
              {busy ? t('signing_in') : t('sign_in')}
            </T>
          </Pressable>

          {/* The reset is a conversation with the owner, not a link in an
              inbox. Saying so here is the entire feature. */}
          <Pressable
            onPress={() => setShowForgot((v) => !v)}
            testID="forgot-password"
            style={{ paddingVertical: 4 }}
          >
            <T size={12.5} weight={700} color={colors.gold} style={{ textAlign: 'center' }}>
              {t('forgot_password')}
            </T>
          </Pressable>
          {showForgot ? (
            <T
              size={12}
              color={colors.textFaint}
              style={{ textAlign: 'center', lineHeight: 18 }}
            >
              {t('forgot_password_hint')}
            </T>
          ) : null}
        </View>

        <Pressable onPress={onSkip} testID="work-offline" style={{ paddingVertical: 10 }}>
          <T size={12.5} weight={700} color={colors.textDim} style={{ textAlign: 'center' }}>
            {t('work_offline')}
          </T>
        </Pressable>

        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 8 }}>
          <LanguageSwitcher />
          <ThemeToggle />
          <TextSizePicker />
        </View>
      </View>
    </ScrollView>
  );
}
